import { AxiosRequestConfig } from 'axios';

type AdditionalFieldFormOption = {
  type: string;
  name: string;
  required: boolean;
  options: string[];
  manages_inventory: boolean;
  read_only: boolean;
  additional_field_id?: string;
};

/**
 * This returns the axios config that allows images/files to be uploaded alongside the store item.
 * This also handles the additional fields that are sent as a part of the store_item.options array.
 * @param files - The files to be uploaded.
 * @returns The axios config.
 *
 * @example
 * const config = constructStoreItemFormDataConfig([file1, file2]);
 * const response = await axios.post('/store_items', config);
 */
const constructStoreItemFormDataConfig = (files?: File[]): AxiosRequestConfig => {
  return {
    headers: {
      'Content-Type': files && files.length > 0 ? 'multipart/form-data' : 'application/json',
    },
    formSerializer: {
      visitor: function (this, value, key, path, helpers): boolean {
        // Handle files array separately
        if (key === 'files' && Array.isArray(value)) {
          for (let i = 0; i < value.length; i++) {
            this.append('store_item[files][]', value[i]);
          }
          return false;
        }

        if (key === 'options' && Array.isArray(value)) {
          if (value.length === 0) {
            // allow empty options array, and send it back explicity as an empty array
            this.append('store_item[options][]', '[]');
            return false;
          }

          value.forEach((option: unknown) => {
            // Type guard for option
            if (
              option &&
              typeof option === 'object' &&
              'type' in option &&
              'name' in option &&
              'required' in option
            ) {
              const typedOption = option as AdditionalFieldFormOption;
              this.append('store_item[options][][type]', typedOption.type);
              this.append('store_item[options][][name]', typedOption.name);
              this.append('store_item[options][][required]', typedOption.required);

              if (typedOption.additional_field_id) {
                this.append(
                  'store_item[options][][additional_field_id]',
                  typedOption.additional_field_id
                );
              }

              this.append(
                'store_item[options][][manages_inventory]',
                typedOption.manages_inventory
              );

              if (Array.isArray(typedOption.options)) {
                typedOption.options.forEach((opt) => {
                  this.append('store_item[options][][options][]', opt);
                });
              }
            }
          });
          return false;
        }

        // When we're in multipart/form-data mode and the path includes store_item,
        // we need to handle nested objects and arrays properly
        if (files && files.length > 0 && path && path.includes('store_item')) {
          // Handle nested arrays inside store_item
          if (Array.isArray(value)) {
            // Build the path string from the path array if it exists
            const pathString = path.join('.');
            const pathPrefix = pathString ? `${pathString}[${key.toString()}]` : key.toString();

            for (let i = 0; i < value.length; i++) {
              // Proper type guard for object checking
              if (value[i] !== null && typeof value[i] === 'object') {
                // For objects inside arrays, handle each property
                const entries = Object.entries(value[i] as Record<string, unknown>);
                entries.forEach(([propKey, propValue]) => {
                  if (propValue !== undefined && propValue !== null) {
                    this.append(`${pathPrefix}[][${propKey}]`, propValue);
                  }
                });
              } else {
                // For primitives in arrays
                this.append(`${pathPrefix}[]`, value[i]);
              }
            }
            return false;
          }

          // For nested objects, let the default visitor handle it
          // as it will create the proper nested path
        }

        return helpers.defaultVisitor.call(this, value, key, path, helpers);
      },
    },
  };
};

export { constructStoreItemFormDataConfig };
